import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { ArrowLeft } from 'lucide-react';

import { CsvBoxImportButton } from '../csv-box-import-button';
import { BaseImportViewProps } from './import-view-props';

export type ProductImportMode = 'add' | 'update';
export type ProductImportAnchorKey = 'auto' | 'url' | 'sku';

interface ViewProductsImportCsvOptionSelectionProps extends BaseImportViewProps {
  flowTitle: string;
  dealId: string;
  onBack: () => void;
  onUploadSuccess?: () => Promise<void>;
  onCloseDialog?: () => void;
  products?: DealItemWithVersions[];
}

export const ViewProductsImportCsvOptionSelection: React.FC<
  ViewProductsImportCsvOptionSelectionProps
> = ({
  flowTitle,
  dealId,
  onBack,
  onUploadSuccess,
  onCloseDialog,
  onEmerchConsentRequested,
  onPricingModeRequested,
  products = [],
}) => {
  const { t } = useTranslation();
  const hasProducts = products.length > 0;
  const [selectedMode, setSelectedMode] = useState<ProductImportMode>('add');
  const [selectedAnchorKey, setSelectedAnchorKey] =
    useState<ProductImportAnchorKey>('auto');

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={onBack}
            aria-label="Revenir au menu"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>{flowTitle}</DialogTitle>
        </div>
        <DialogDescription>
          {t('components.import_flow.products_option_selection.choose_option')}{' '}
          :
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-6">
        <RadioGroup
          value={selectedMode}
          onValueChange={(value) => setSelectedMode(value as ProductImportMode)}
          className="space-y-4"
        >
          <div className="flex items-start space-x-3">
            <RadioGroupItem value="add" id="add" className="mt-1" />
            <div className="space-y-1">
              <Label
                htmlFor="add"
                className="cursor-pointer text-base font-medium"
              >
                {t(
                  'components.import_flow.products_option_selection.add_new_products'
                )}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t(
                  'components.import_flow.products_option_selection.add_new_products_description'
                )}
              </p>
            </div>
          </div>

          {hasProducts && (
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="update" id="update" className="mt-1" />
              <div className="space-y-1">
                <Label
                  htmlFor="update"
                  className="cursor-pointer text-base font-medium"
                >
                  {t(
                    'components.import_flow.products_import_csv_option_selection.update_existing_products'
                  )}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'components.import_flow.products_import_csv_option_selection.update_existing_products_description'
                  )}
                </p>
              </div>
            </div>
          )}
        </RadioGroup>

        {hasProducts && selectedMode === 'update' ? (
          <div className="flex items-start space-x-3">
            {/* Spacer to align with radio labels */}
            <div className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />

            <div className="w-full space-y-2">
              <p className="text-sm text-muted-foreground">
                {t(
                  'components.import_flow.products_import_csv_option_selection.anchor_key_label'
                )}
              </p>

              <Select
                value={selectedAnchorKey}
                onValueChange={(value) =>
                  setSelectedAnchorKey(value as ProductImportAnchorKey)
                }
              >
                <SelectTrigger className="h-8 w-full bg-muted/40 px-3 py-1 text-sm text-muted-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] text-sm"
                  viewportClassName="max-w-full"
                >
                  <SelectItem
                    value="auto"
                    className="whitespace-normal break-words py-1.5"
                  >
                    {t(
                      'components.import_flow.products_import_csv_option_selection.anchor_key_auto'
                    )}
                  </SelectItem>
                  <SelectItem
                    value="url"
                    className="whitespace-normal break-words py-1.5"
                  >
                    {t(
                      'components.import_flow.products_import_csv_option_selection.anchor_key_url'
                    )}
                  </SelectItem>
                  <SelectItem
                    value="sku"
                    className="whitespace-normal break-words py-1.5"
                  >
                    {t(
                      'components.import_flow.products_import_csv_option_selection.anchor_key_sku'
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        <div className="flex justify-end">
          <CsvBoxImportButton
            dealId={dealId}
            instructionMode={selectedMode}
            anchorKey={selectedMode === 'update' ? selectedAnchorKey : 'auto'}
            onUploadSuccess={onUploadSuccess}
            onCloseModal={onCloseDialog}
            onEmerchConsentRequested={
              selectedMode === 'add' ? onEmerchConsentRequested : undefined
            }
            // Mode de calcul des prix : pertinent aussi en update (on peut
            // vouloir recalculer les prix des produits existants, ou choisir
            // « je ne mets pas de prix à jour »). Donc transmis dans les 2 modes.
            onPricingModeRequested={onPricingModeRequested}
          />
        </div>
      </div>
    </>
  );
};
